/**
 * Auth context. v1-final implementation: access token lives in module-level
 * memory inside services/api.ts; this context surfaces the current user
 * record and triggers eager refresh on mount to restore sessions from the
 * HttpOnly refresh cookie. See ARCHITECTURE.md §4.6.1.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { auth as authApi, type SignupInput } from "../services/auth";
import {
  apiCall,
  setAccessToken,
  tryEagerRefresh,
  serverLogout,
} from "../services/api";
import type { PublicUser } from "../types";

interface AuthContextValue {
  user: PublicUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ ok: true } | { ok: false; message: string }>;
  signup: (input: SignupInput) => Promise<
    { ok: true } | { ok: false; message: string; errors?: Array<{ field?: string; message: string }> }
  >;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // On mount, attempt to revive a session by hitting /api/auth/refresh. The
  // refresh cookie (HttpOnly) is sent automatically by the browser. If it's
  // valid, we get back a fresh access token + user; if not, we proceed as
  // logged-out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const refreshed = await tryEagerRefresh();
      if (cancelled) return;
      if (refreshed) {
        const me = await apiCall("/api/auth/me");
        if (!cancelled && me.ok && me.data.user) setUser(me.data.user);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback<AuthContextValue["login"]>(async (email, password) => {
    const res = await authApi.login(email, password);
    if (res.ok && res.data.token && res.data.user) {
      setAccessToken(res.data.token);
      setUser(res.data.user);
      return { ok: true };
    }
    return { ok: false, message: res.ok ? "Login failed" : res.error.message };
  }, []);

  const signup = useCallback<AuthContextValue["signup"]>(async (input) => {
    const res = await authApi.signup(input);
    if (res.ok && res.data.token && res.data.user) {
      setAccessToken(res.data.token);
      setUser(res.data.user);
      return { ok: true };
    }
    if (res.ok) return { ok: false, message: "Signup failed" };
    return { ok: false, message: res.error.message, errors: res.error.errors };
  }, []);

  const logout = useCallback(async () => {
    await serverLogout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, signup, logout }),
    [user, loading, login, signup, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
